import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchSavedTrends, deleteSavedTrend } from '@/services/global-trends'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import { TrendingUp, Sparkles } from 'lucide-react'

interface SavedTrendItem {
  ID: string
  saved_at: number
  trend_id?: string
  geo?: string
  category?: string
  websearch_query?: string
  summary?: string
  quantity?: number
  trendScore?: number
  sources?: { title: string; url: string }[]
}

export default function SavedTrendsPage() {
  const [items, setItems] = useState<SavedTrendItem[]>([])
  const [lastKey, setLastKey] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = async (reset = true) => {
    setLoading(true)
    try {
      const resp = await fetchSavedTrends({ lastKey: reset ? undefined : lastKey, limit: 20 })
      const next = resp.items as SavedTrendItem[]
      setItems((prev) => (reset ? next : [...prev, ...next]))
      setLastKey(resp.last_evaluated_key || null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(true)
  }, [])

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Saved Trends</h1>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((it) => (
          <Card key={it.ID} className="transition-all hover:shadow-md hover:shadow-black/5 dark:hover:shadow-white/5 border border-border/50 bg-card/50 backdrop-blur-sm">
            <CardHeader className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <CardTitle className="text-lg font-semibold text-foreground line-clamp-1">{it.websearch_query}</CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    <span className="line-clamp-1">{it.summary}</span>
                  </CardDescription>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">{it.geo}</Badge>
                  <Badge variant="outline" className="text-xs">{it.category}</Badge>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="inline-flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="font-medium text-foreground">{it.trendScore ?? 0}</span>
                  <span>trend score</span>
                </div>
                {typeof it.quantity === 'number' && it.quantity > 0 ? (
                  <div className="inline-flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
                    <span className="font-medium text-foreground">{it.quantity.toLocaleString()}</span>
                    <span>mentions</span>
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {it.summary && (
                <div className="text-sm text-muted-foreground">
                  <details className="transition-all">
                    <summary className="cursor-pointer select-none py-2 px-1 rounded hover:bg-muted/40">
                      Read more
                    </summary>
                    <div className="mt-3 space-y-3">
                      <MarkdownRenderer content={it.summary} />
                      {it.sources?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {it.sources.map((s, i) => (
                            <a
                              key={i}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs underline underline-offset-2"
                            >
                              {s.title}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </details>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Saved at {new Date((it.saved_at || 0) * 1000).toLocaleString()}
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={async () => {
                    await deleteSavedTrend(it.ID)
                    setItems((prev) => prev.filter((p) => p.ID !== it.ID))
                  }}
                >
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {loading && items.length === 0 && (
          [...Array(4)].map((_, i) => (
            <Card key={i} className="border border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Skeleton className="h-5 w-12" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Skeleton className="h-6 w-24" />
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {lastKey && (
        <div>
          <Button size="sm" variant="secondary" disabled={loading} onClick={() => load(false)}>
            {loading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
